import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, Wifi, DollarSign, Activity, BarChart3, TrendingUp, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { DemoAccountsManager } from '@/components/demo/DemoAccountsManager';
import { CreateDemoAccountModal } from '@/components/demo/CreateDemoAccountModal';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface AccountsPageProps {
  onPageChange: (page: string) => void;
}

interface SummaryStats {
  totalCapital: number;
  activeStrategies: number;
  todayTrades: number;
  demoCount: number;
}

interface BrokerConnection {
  id: string;
  broker_name: string;
  status: string;
  token_expires_at: string | null;
  updated_at: string | null;
}

interface BrokerBalanceData {
  available_cash: number;
  used_margin: number;
  total_equity: number;
  currency: string;
}

const PulseDot = ({ status, lastSync }: { status: string; lastSync?: string | null }) => {
  const isStale = lastSync
    ? Date.now() - new Date(lastSync).getTime() > 10 * 60 * 1000
    : true;

  if (status === 'error' || status === 'expired') {
    return <span className="relative flex h-2.5 w-2.5"><span className="h-2.5 w-2.5 rounded-full bg-destructive" /></span>;
  }
  if (status === 'connected' && !isStale) {
    return (
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
      </span>
    );
  }
  if (status === 'connected' && isStale) {
    return <span className="relative flex h-2.5 w-2.5"><span className="h-2.5 w-2.5 rounded-full bg-yellow-500" /></span>;
  }
  return null;
};

const AccountsPage = ({ onPageChange }: AccountsPageProps) => {
  const { user } = useAuth();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [stats, setStats] = useState<SummaryStats>({ totalCapital: 0, activeStrategies: 0, todayTrades: 0, demoCount: 0 });
  const [brokerConnections, setBrokerConnections] = useState<BrokerConnection[]>([]);
  const [balances, setBalances] = useState<Record<string, { balance: BrokerBalanceData; last_synced: string } | null>>({});
  const [loadingBalances, setLoadingBalances] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!user) return;
    const fetchStats = async () => {
      const { data: accounts } = await supabase.from('demo_accounts').select('balance').eq('user_id', user.id);
      const totalCapital = accounts?.reduce((sum, a) => sum + Number(a.balance), 0) ?? 0;
      const demoCount = accounts?.length ?? 0;
      const { count: activeStrategies } = await supabase.from('strategy_instances').select('id', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'running');
      const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
      const { count: todayTrades } = await supabase.from('strategy_orders').select('id', { count: 'exact', head: true }).eq('user_id', user.id).gte('created_at', todayStart.toISOString());
      setStats({ totalCapital, activeStrategies: activeStrategies ?? 0, todayTrades: todayTrades ?? 0, demoCount });
    };
    const fetchBrokers = async () => {
      const { data } = await supabase.from('broker_connections').select('id, broker_name, status, token_expires_at, updated_at').eq('user_id', user.id).in('status', ['connected', 'expired', 'error']);
      setBrokerConnections(data ?? []);
    };
    fetchStats();
    fetchBrokers();
  }, [user]);

  const fetchBrokerBalance = useCallback(async (connId: string) => {
    setLoadingBalances(prev => ({ ...prev, [connId]: true }));
    try {
      const { data, error } = await supabase.functions.invoke('fetch-broker-balance', {
        body: { broker_connection_id: connId },
      });
      if (error) throw error;
      if (data?.error === 'token_expired') {
        toast.error('Broker token expired. Please re-authenticate.');
        setBrokerConnections(prev => prev.map(c => c.id === connId ? { ...c, status: 'expired' } : c));
        return;
      }
      if (data?.error) { toast.error(data.error); return; }
      setBalances(prev => ({ ...prev, [connId]: { balance: data.balance, last_synced: data.last_synced } }));
    } catch (err: any) {
      toast.error(err.message || 'Failed to fetch balance');
    } finally {
      setLoadingBalances(prev => ({ ...prev, [connId]: false }));
    }
  }, []);

  const isTokenExpiringSoon = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt).getTime() - Date.now() < 2 * 60 * 60 * 1000;
  };

  const summaryItems = [
    { label: 'Total Virtual Capital', value: `$${stats.totalCapital.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, icon: DollarSign },
    { label: 'Active Strategies', value: String(stats.activeStrategies), icon: Activity },
    { label: 'Trades Today', value: String(stats.todayTrades), icon: BarChart3 },
  ];

  const connectedBrokers = brokerConnections.filter(c => ['connected', 'expired', 'error'].includes(c.status));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Accounts</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your demo and live trading accounts</p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> New Demo Account
        </Button>
      </div>

      <Tabs defaultValue="demo">
        <TabsList>
          <TabsTrigger value="demo" className="gap-1.5">
            Demo
            {stats.demoCount > 0 && <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 text-[10px]">{stats.demoCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="live" className="gap-1.5">
            Live
            <Badge variant="outline" className="ml-1 h-5 min-w-[20px] px-1.5 text-[10px]">{connectedBrokers.length}</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="demo" className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {summaryItems.map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.label} className="bg-card/60 border-border/30">
                  <CardContent className="flex items-center gap-3 p-4">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground truncate">{item.label}</p>
                      <p className="text-lg font-semibold tabular-nums">{item.value}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <DemoAccountsManager />
        </TabsContent>

        <TabsContent value="live" className="space-y-4">
          {connectedBrokers.length === 0 ? (
            <Card className="border-dashed border-2 border-border/40">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-14 h-14 rounded-full bg-muted/40 flex items-center justify-center mb-4">
                  <Wifi className="w-6 h-6 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Connect your broker</h3>
                <p className="text-sm text-muted-foreground max-w-sm mb-6">
                  Connect Zerodha, Angel One, Dhan, Upstox and more. Your strategies will execute live trades automatically.
                </p>
                <Button onClick={() => onPageChange('brokers')} className="gap-2">
                  <TrendingUp className="h-4 w-4" /> Connect Broker
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {connectedBrokers.map((conn) => {
                const bal = balances[conn.id];
                const loading = loadingBalances[conn.id];
                const expiringSoon = isTokenExpiringSoon(conn.token_expires_at);
                const isExpired = conn.status === 'expired';
                const isError = conn.status === 'error';
                const formattedSync = conn.updated_at
                  ? formatDistanceToNow(new Date(conn.updated_at), { addSuffix: true })
                  : null;

                return (
                  <Card key={conn.id} className={`${isExpired || isError ? 'border-destructive/50' : 'border-border/30'}`}>
                    <CardContent className="p-5 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <PulseDot status={conn.status} lastSync={conn.updated_at} />
                          <h3 className="font-semibold capitalize">{conn.broker_name}</h3>
                          {isExpired ? (
                            <Badge variant="destructive" className="text-[10px]">Expired</Badge>
                          ) : isError ? (
                            <Badge variant="destructive" className="text-[10px]">Error</Badge>
                          ) : expiringSoon ? (
                            <Badge variant="secondary" className="text-[10px] bg-yellow-500/10 text-yellow-600">Expiring Soon</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-[10px] bg-green-500/10 text-green-600">Connected</Badge>
                          )}
                        </div>
                        {isExpired || isError ? (
                          <Button size="sm" onClick={() => onPageChange('brokers')} className="gap-1.5">
                            <RefreshCw className="h-3 w-3" /> Reconnect
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={loading}
                            onClick={() => fetchBrokerBalance(conn.id)}
                            className="gap-1.5"
                          >
                            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                          </Button>
                        )}
                      </div>

                      {isExpired && (
                        <div className="flex items-center gap-2 text-sm text-destructive">
                          <AlertTriangle className="h-4 w-4" />
                          <span>Token expired. Please re-authenticate on the Brokers page.</span>
                        </div>
                      )}

                      {isError && (
                        <div className="flex items-center gap-2 text-sm text-destructive">
                          <AlertTriangle className="h-4 w-4" />
                          <span>Connection error after multiple health check failures.</span>
                        </div>
                      )}

                      {expiringSoon && !isExpired && !isError && (
                        <div className="flex items-center gap-2 text-sm text-yellow-600">
                          <AlertTriangle className="h-4 w-4" />
                          <span>Token expires soon. Consider re-authenticating.</span>
                        </div>
                      )}

                      {bal ? (
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <p className="text-xs text-muted-foreground">Available</p>
                            <p className="text-sm font-semibold tabular-nums">
                              {bal.balance.currency} {bal.balance.available_cash.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Used Margin</p>
                            <p className="text-sm font-semibold tabular-nums">
                              {bal.balance.currency} {bal.balance.used_margin.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Total Equity</p>
                            <p className="text-sm font-semibold tabular-nums">
                              {bal.balance.currency} {bal.balance.total_equity.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      ) : !loading && conn.status === 'connected' ? (
                        <p className="text-sm text-muted-foreground">Click Refresh to load balance</p>
                      ) : loading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <RefreshCw className="h-3 w-3 animate-spin" /> Fetching balance...
                        </div>
                      ) : null}

                      {formattedSync && (
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                          <CheckCircle className="h-3 w-3" />
                          Last synced {formattedSync}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              <Card className="border-dashed border-2 border-border/40">
                <CardContent className="flex flex-col items-center justify-center py-10">
                  <Button variant="outline" onClick={() => onPageChange('brokers')} className="gap-2">
                    <Plus className="h-4 w-4" /> Connect Another Broker
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <CreateDemoAccountModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onAccountCreated={() => window.location.reload()}
      />
    </div>
  );
};

export default AccountsPage;
